<template>
  <HeaderContent title="Offer Document" />
  <ActionContent
    ctaText="Create Offer"
    :filterDate="true"
    v-model:startDate="startDate"
    v-model:endDate="endDate"
    :filterModal="false"
    :ctaOptions="createOptions"
    @search-query="searchData"
  />

  <DataTable
    :data="offerList"
    :header="headers"
    :custom-actions="customActions"
  />
</template>

<script setup>
import axios from "axios";
import HeaderContent from "@/components/HeaderContent.vue";
import ActionContent from "@/components/ActionContent.vue";
import DataTable from "@/components/DataTable.vue";
import { ref, onMounted, inject } from "vue";
import { useLoading } from "vue-loading-overlay";
import { useAuthStore } from "@/store/auth.js";
import { useToast } from "vue-toastification";
import { useRouter } from "vue-router";
import { formatRupiah, formatDate } from "@/utils/format";
// import dayjs from "dayjs";

const auth = useAuthStore();
const $loading = useLoading();
const swal = inject("$swal");
const toast = useToast();
const router = useRouter();

const headers = ref([
  {
    key: "no",
    label: "No",
    thClass: "text-center px-1",
    tdClass: "text-center px-1",
  },
  {
    key: "offer_no",
    label: "Offer No",
    sortable: true,
    thClass: "text-left",
    tdClass: "text-left font-weight-bold",
  },
  {
    key: "category",
    label: "Category",
    sortable: true,
    thClass: "text-center",
    tdClass: "text-center",
    isHtml: true,
  },
  {
    key: "guest_name_format",
    label: "Guest",
    sortable: true,
    thClass: "text-left",
    tdClass: "text-left",
  },
  {
    key: "name",
    label: "Offer Name",
    sortable: true,
    thClass: "text-left",
    tdClass: "text-left",
  },
  {
    key: "start",
    label: "Arrival",
    sortable: true,
    thClass: "text-left",
    tdClass: "text-left",
    formatter: (value) => formatDate(value),
  },
  {
    key: "end",
    label: "Departure",
    sortable: true,
    thClass: "text-left",
    tdClass: "text-left",
    formatter: (value) => formatDate(value),
  },
  {
    key: "grand_total_format",
    label: "Grand Total",
    sortable: true,
    thClass: "text-left",
    tdClass: "text-left",
  },
  {
    key: "paid_format",
    label: "Paid",
    thClass: "text-left",
    tdClass: "text-left text-success",
  },
  {
    key: "remaining_format",
    label: "Remaining",
    thClass: "text-left",
    tdClass: "text-left text-danger",
  },
  {
    key: "payment_status",
    label: "Payment Status",
    sortable: true,
    thClass: "text-left",
    tdClass: "text-left",
  },
  {
    key: "status_offer",
    label: "Status",
    sortable: false,
    thClass: "text-center",
    tdClass: "text-center",
  },
  {
    key: "accurate_status",
    label: "Accurate",
    sortable: false,
    thClass: "text-center",
    tdClass: "text-center",
  },
  { key: "action", label: "Action" },
]);

const customActions = ref([
  {
    label: (item) => (item.accurate_id ? "Re-sync Accurate" : "Sync Accurate"),
    variant: "info",
    icon: "fas fa-sync",
    action: (item) => {
      syncToAccurate(item.uuid);
    },
    showIf: (item) =>
      !item.accurate_synced &&
      item.payment_status === "Fullpayment" &&
      (item.status_offer === "Approved" ||
        item.status_offer === "Fullpayment" ||
        item.status_offer === "Completed"),
  },
  {
    label: "Detail",
    variant: "primary",
    icon: "fas fa-info-circle",
    action: (item) => {
      if (item.uuid) {
        const path =
          item.category === "Agent" ? "offer-agent" : "offerdocuments";
        router.push(`/${path}/detail/${item.uuid}`);
      }
    },
  },
  {
    label: "Edit",
    variant: "success",
    icon: "fas fa-edit",
    showIf: (item) => item.status_offer !== "Cancelled",
    action: (item) => {
      if (item.uuid) {
        const path =
          item.category === "Agent" ? "offer-agent" : "offerdocuments";
        router.push(`/${path}/edit/${item.uuid}`);
      }
    },
  },
  {
    label: "Delete",
    variant: "danger",
    icon: "fas fa-trash",
    action: (item) => {
      swal({
        title: "Confirm",
        text: "Are you sure to delete this offer?",
        showCancelButton: true,
        confirmButtonText: "Yes, delete!",
        cancelButtonText: "Cancel",
        customClass: {
          popup: "rounded",
          confirmButton: "btn btn-danger me-2",
          cancelButton: "btn btn-secondary",
        },
        buttonsStyling: false,
      }).then((confirm) => {
        if (confirm.value) {
          deleteData(item.uuid);
        } else {
          swal.close();
        }
      });
    },
  },
]);

const searchData = async (search) => {
  const loader = $loading.show({
    color: "#489464",
    loader: "spinner",
  });
  try {
    await getAll(search);
  } finally {
    loader.hide();
  }
};

const addOffer = () => {
  router.push("/offerdocuments/add");
};

const addAgentOffer = () => {
  router.push("/offer-agent/add");
};

const createOptions = ref([
  { label: "Standard Offer", action: addOffer },
  { label: "Agent Offer", action: addAgentOffer },
]);

const startDate = ref("");
const endDate = ref("");

let offerList = ref([]);

const getAll = async (search = "") => {
  const response = await axios.get(`${process.env.VUE_APP_API_URL}/offer`, {
    params: {
      search: search,
      start_date: startDate.value,
      end_date: endDate.value,
    },
    headers: {
      "Content-type": "application/json",
      Authorization: "Bearer " + auth._token,
    },
  });

  const res = response.data;

  if (res.status) {
    offerList.value = res.data.map((i) => {
      // format currency helper for displaying paid amount correctly based on currency
      let paid = i.paid || 0;
      let remaining = i.remaining || 0;
      let formattedPaid = formatRupiah(paid);
      if (i.currency && i.currency.code !== "IDR") {
        const locale = i.currency.locale || "en-US";
        const formatter = new Intl.NumberFormat(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        formattedPaid = `${i.currency.symbol} ${formatter.format(
          Number(paid),
        )}`;
      }

      return {
        ...i,
        guest_name_format: i.guest
          ? i.guest.alias
            ? `${i.guest.alias}. ${i.guest.fullname}`
            : i.guest.fullname
          : "-",
        grand_total_format: formatRupiah(i.grand_total_final),
        paid_format: formattedPaid,
        remaining_format: formatRupiah(remaining),
        status_offer: i.status,
        accurate_status: i.accurate_synced ? "Synced" : (i.accurate_id ? "Need Re-sync" : "Pending"),
      };
    });
  } else {
    toast.error(res.message);
  }
};

const deleteData = async (uuid) => {
  if (uuid) {
    toast.clear();
    const loader = $loading.show({
      color: "#009689",
      loader: "bars",
      zIndex: 999999,
    });

    try {
      const response = await axios.delete(
        `${process.env.VUE_APP_API_URL}/offer/delete/${uuid}`,
        {
          headers: {
            "Content-type": "application/json",
            Authorization: "Bearer " + auth._token,
          },
        },
      );

      const res = response.data;

      if (res.status) {
        toast.success(res.message);
        getAll();
      }
    } finally {
      loader.hide();
    }
  }
};

const syncToAccurate = async (uuid) => {
  const loader = $loading.show({
    color: "#489464",
    loader: "spinner",
  });
  try {
    const response = await axios.post(
      `${process.env.VUE_APP_API_URL}/offer/sync/${uuid}`,
      {},
      {
        headers: {
          "Content-type": "application/json",
          Authorization: "Bearer " + auth._token,
        },
      },
    );

    const res = response.data;

    if (res.status) {
      toast.success("Offer successfully synced to Accurate!");
      getAll();
    } else {
      toast.error(res.message || "Sync failed");
    }
  } catch (e) {
    toast.error(e.response?.data?.message ?? "Sync failed");
  } finally {
    loader.hide();
  }
};

import { watch } from "vue";

watch([startDate, endDate], () => {
  getAll();
});

onMounted(async () => {
  const loader = $loading.show({
    color: "#489464",
    loader: "spinner",
  });
  try {
    await getAll();
  } finally {
    loader.hide();
  }
});
</script>
